// The figures at the top of Pricing & promotions (spec/13 §2, §18): what is on sale, what is running and what an hour
// costs — the three lists the tabs below already read, one cache entry each, shown as a count. A figure whose list the
// member may not read is left out rather than shown as a zero.

import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { HeroStat } from '@clavian/ui-web';
import { useState, type ReactNode } from 'react';

import { useAccess } from '../../api/queries';
import { useScopeIds } from '../../shell/scope';
import { usePassProducts } from '../passes/queries';
import { usePromotions, useRateCard } from './queries';
import { offerStatus } from './terms';

const DISCOUNTS_READ = 'discounts:read';
const DISCOUNTS_MANAGE = 'discounts:manage';
const PRICING_READ = 'pricing:read';

export function PricingHero({ venueId }: { readonly venueId: string | undefined }): ReactNode {
  const ids = useScopeIds();
  const access = useAccess(ids.access);
  const ready = access.status === 'ready';
  const canPromotions = ready && (access.can(DISCOUNTS_READ) || access.can(DISCOUNTS_MANAGE));
  const canRate = ready && access.can(PRICING_READ) && venueId !== undefined;
  return (
    <>
      {venueId === undefined ? null : <PassesStat organizationId={ids.organizationId} venueId={venueId} />}
      {canPromotions ? <PromotionsStat organizationId={ids.organizationId} /> : null}
      {canRate ? <RateStat organizationId={ids.organizationId} venueId={venueId} /> : null}
    </>
  );
}

function PassesStat({ organizationId, venueId }: { readonly organizationId: string; readonly venueId: string }): ReactNode {
  const { t } = useTranslation('console');
  const products = usePassProducts(organizationId, venueId);
  if (products.data === undefined) return null;
  const onSale = products.data.filter((product) => product.active).length;
  return <HeroStat label={t('pricing.hero.passes')} value={onSale} of={t('pricing.hero.of', { total: products.data.length })} />;
}

function PromotionsStat({ organizationId }: { readonly organizationId: string }): ReactNode {
  const { t } = useTranslation('console');
  const promotions = usePromotions(organizationId);
  const [now] = useState(() => Date.now());
  if (promotions.data === undefined) return null;
  const running = promotions.data.filter((promotion) => offerStatus(promotion, now) === 'active').length;
  return <HeroStat label={t('pricing.hero.promotions')} value={running} of={t('pricing.hero.of', { total: promotions.data.length })} />;
}

function RateStat({ organizationId, venueId }: { readonly organizationId: string; readonly venueId: string }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const rateCard = useRateCard(organizationId, venueId);
  if (rateCard.data === undefined) return null;
  return <HeroStat label={t('pricing.hero.hour')} value={format.money(rateCard.data.fallback.hourlyAmount)} />;
}
